<template>
    <v-container fluid>
      <!-- <v-card> -->
        <v-card-title>
          <v-icon class="me-2">mdi-newspaper</v-icon>
          Articles
        </v-card-title>
        <v-card-subtitle> Manage your articles here. </v-card-subtitle>
        <v-card-actions>
          <v-btn @click="openAddDialog" color="primary">
            <v-icon left>mdi-plus</v-icon>
            Add Article
          </v-btn>
        </v-card-actions>
        <v-card-text>
          <!-- Articles List -->
          <v-data-iterator
            :items="articles"
            :items-per-page="itemsPerPage"
            v-model:search="search"
            :loading="loading"
          >
            <template v-slot:header="{ page, pageCount, prevPage, nextPage }">
              <h1 class="text-h4 font-weight-bold d-flex justify-space-between mb-4 align-center">
                <v-text-field
                  v-model="search"
                  density="compact"
                  label="Search"
                  prepend-inner-icon="mdi-magnify"
                  style="max-width: 300px"
                  variant="solo-filled"
                  clearable
                  hide-details
                  single-line
                ></v-text-field>
  
                <div class="d-flex align-center">
                  <div class="d-inline-flex">
                    <v-btn
                      :disabled="page == 1"
                      class="me-2"
                      icon="mdi-arrow-left"
                      size="small"
                      variant="tonal"
                      @click="prevPage"
                    ></v-btn>
  
                    <v-btn
                      :disabled="page == pageCount"
                      icon="mdi-arrow-right"
                      size="small"
                      variant="tonal"
                      @click="nextPage"
                    ></v-btn>
                  </div>
                </div>
              </h1>
            </template>
  
            <template v-slot:default="{ items }">
              <v-row v-if="items.length == 0" class="justify-center">
                <v-col class="text-center" cols="12">
                  <v-alert type="info" border="left" icon="mdi-information">
                    No Articles Available
                  </v-alert>
                </v-col>
              </v-row>
              <v-row else>
                <v-col v-for="(item, i) in items" :key="i" cols="12" sm="6" xl="4">
                  <v-card>
                    <v-img :src="getImageUrl(item.raw.image)" height="200" contain></v-img>
  
                    <v-card-title>{{ item.raw.title }}</v-card-title>
                    <v-card-text>{{ item.raw.author }}</v-card-text>
  
                    <v-card-actions>
                      <v-btn @click="openViewArticleDialog(item.raw)" color="primary">
                        <v-icon left>mdi-eye</v-icon>
                        View
                      </v-btn>
                      <v-btn @click="openEditDialog(item.raw)" color="primary">
                        <v-icon left>mdi-pencil</v-icon>
                        Edit
                      </v-btn>
                      <v-btn @click="openDeleteDialog(item.raw.article_id)" color="red">
                        <v-icon left>mdi-delete</v-icon>
                        Delete
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>
            </template>
  
            <template v-slot:loader>
              <v-row>
                <v-col
                  v-for="(_, k) in [0, 1, 2, 3]"
                  :key="k"
                  cols="12"
                  sm="6"
                  xl="3"
                >
                  <v-skeleton-loader
                    class="border"
                    type="image, article"
                    :loading="loading"
                  ></v-skeleton-loader>
                </v-col>
              </v-row>
            </template>
          </v-data-iterator>
        </v-card-text>
      <!-- </v-card> -->
    </v-container>
  
    <!-- Article Dialog-->
    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card>
        <v-toolbar>
          <v-toolbar-title flat>{{ editMode ? "Edit Article" : "Add Article" }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <v-form @submit.prevent="saveArticle">
            <v-text-field
              v-model="currentArticle.title"
              label="Article Title"
              required
            ></v-text-field>
            <v-textarea
              v-model="currentArticle.content"
              label="Content"
              required
            ></v-textarea>
            <v-text-field
              v-model="currentArticle.author"
              label="Author"
              required
            ></v-text-field>
  
            <v-file-input
              v-model="currentArticle.image"
              label="Article Image"
              accept="image/*"
              @change="handleImageChange"
            ></v-file-input>
  
            <v-card-actions class="justify-end">
              <v-btn type="submit" color="primary">Save</v-btn>
            </v-card-actions>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  
    <!-- Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title class="headline">Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete this article?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="confirmArticleDelete" color="red" text> Delete </v-btn>
          <v-btn @click="deleteDialog = false" color="grey" text>
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  
    <!-- View Article Dialog -->
<v-dialog v-model="viewArticleDialog" max-width="600px">
  <v-card>
    <v-card-title class="d-flex justify-space-between align-center">
      <div class="text-h5 text-medium-emphasis ps-2">
        {{ currentArticle.title }}
      </div>
      <v-btn icon variant="text" @click="viewArticleDialog = false">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-divider class="mb-4"></v-divider>

    <v-card-text>
      <!-- Article Image -->
      <v-img
        v-if="currentArticle.image"
        :src="getImageUrl(currentArticle.image)"
        height="300px"
        contain
      ></v-img>
      <!-- Fallback Image if no image exists -->
      <v-img
        v-else
        :src="'/path/to/default/image.jpg'"
        height="300px"
        contain
      ></v-img>

      <!-- Title, Author, and Content -->
      <div class="mt-4">
        <div class="text-h6 font-weight-medium">{{ currentArticle.title }}</div>
        <div class="mt-2 text-body2">
          <strong>Author:</strong> {{ currentArticle.author }}
        </div>
        <div class="mt-4" style="white-space: pre-line;">
          <strong>Content:</strong>
          <div class="mt-2">{{ currentArticle.content }}</div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</v-dialog>

  
    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.visible"
      multi-line
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    >
      {{ snackbar.message }}
    </v-snackbar>
  </template>
  
  <script>
  export default {
    data() {
      return {
        articles: [],
        currentArticle: {
          article_id: null,
          title: "",
          content: "",
          image: null,
          author: ""
        },
        loading: false,
        search: "",
        itemsPerPage: 10,
        dialog: false,
        deleteDialog: false,
        editMode: false,
        viewArticleDialog: false,
        snackbar: {
          visible: false,
          message: "",
          color: "success",
          timeout: 3000
        }
      };
    },
    async created() {
      await this.fetchArticles();
    },
    methods: {
      async fetchArticles() {
        this.loading = true;
        try {
          const response = await this.$axios.get("/article", {
            headers: {
              "Authorization": "Bearer " + localStorage.getItem("token")
            }
          });
          this.articles = response.data.data || [];
        } catch (error) {
          console.error("Error fetching articles:", error);
        } finally {
          this.loading = false;
        }
      },
      getImageUrl(imageName) {
        console.log("Image name:", imageName);
        if (!imageName) {
          return "@/assets/default-image.png";
        }
        const baseUrl = "http://127.0.0.1:8000/storage/images/";
        return `${baseUrl}${imageName}`;
      },
      openAddDialog() {
        this.currentArticle = {
          article_id: null,
          title: "",
          content: "",
          image: null,
          author: ""
        };
        this.editMode = false;
        this.dialog = true;
      },
      openEditDialog(article) {
        this.currentArticle = { ...article };
        this.editMode = true;
        this.dialog = true;
      },
      openViewArticleDialog(article) {
        this.currentArticle = { ...article }; // Populate selectedArticle
        this.viewArticleDialog = true; // Open the dialog
      },
      async saveArticle() {
        const formData = new FormData();
        formData.append("title", this.currentArticle.title || "");
        formData.append("slug", this.currentArticle.slug || "");
        formData.append("content", this.currentArticle.content || "");
        formData.append("author", this.currentArticle.author || "");
  
        if (this.currentArticle.image) {
          formData.append("image", this.currentArticle.image);
        }
  
        try {
          if (this.editMode) {
            await this.$axios.post(`/article/${this.currentArticle.article_id}`, formData, {
              headers: {
                "Content-Type": "multipart/form-data",
                "Authorization": "Bearer " + localStorage.getItem("token")
              }
            });
          } else {
            await this.$axios.post("/article", formData, {
              headers: {
                "Content-Type": "multipart/form-data",
                "Authorization": "Bearer " + localStorage.getItem("token")
              }
            });
          }
          await this.fetchArticles();
          this.dialog = false;
        } catch (error) {
          console.error("Error saving article:", error);
        }
      },
      openDeleteDialog(articleId) {
        this.currentArticle.article_id = articleId;
        this.deleteDialog = true;
      },
      async confirmArticleDelete() {
        try {
          await this.$axios.delete(`/article/${this.currentArticle.article_id}`, {
            headers: {
              "Authorization": "Bearer " + localStorage.getItem("token")
            }
          });
          await this.fetchArticles(); // Refresh articles list
          this.deleteDialog = false;
        } catch (error) {
          console.error("Error deleting article:", error);
        }
      },
      closeDialog() {
        this.dialog = false;
      }
    }
  };
  </script>
  